import { getFieldDefinition } from './registry';
import type { ColumnDef, CompiledFieldNode, CompiledForm } from '../model/types';

export interface ReportTable {
  columns: ColumnDef[];
  rows: string[][];
}

export interface BuildReportOptions {
  fieldIds?: string[];
  useReportTags?: boolean;
  dropColumnsHiddenInAllRows?: boolean;
}

function defaultFieldIds(template: CompiledForm, options: BuildReportOptions): string[] | undefined {
  if (options.fieldIds) return options.fieldIds;
  if (options.useReportTags === false) return undefined;
  const tagged = template.fieldOrder.filter((fieldId) => template.fields[fieldId].tags.report);
  return tagged.length > 0 ? tagged : undefined;
}

function selectedFields(template: CompiledForm, fieldIds?: string[]): CompiledFieldNode[] {
  const wanted = fieldIds ? new Set(fieldIds) : null;
  return template.fieldOrder
    .map((fieldId) => template.fields[fieldId])
    .filter((field) => wanted === null || wanted.has(field.id));
}

function cellsFor(field: CompiledFieldNode, response: CompiledForm, width: number): string[] {
  const answered = response.fields[field.id];
  const definition = getFieldDefinition(field.element);
  if (!answered || !definition || answered.state.hiddenByRule) return Array<string>(width).fill('');
  return definition.cells(field.element, answered.value);
}

// `responses` decide the cells: a field hidden by a rule in one response leaves that response's
// cells blank. Without an explicit selection the report-tagged fields are the column set; a matrix
// with no tag exports every field.
export function buildReportTable(
  template: CompiledForm,
  responses: CompiledForm[],
  options: BuildReportOptions = {},
): ReportTable {
  const groups = selectedFields(template, defaultFieldIds(template, options)).map((field) => {
    const definition = getFieldDefinition(field.element);
    const columns = definition ? definition.columns(field.element, field.label) : [];
    return { field, columns };
  });

  const drop = options.dropColumnsHiddenInAllRows ?? true;
  // The column set is decided before any cell is built: with thousands of responses, building
  // the rows first and rebuilding them after a column is dropped walks every response twice.
  const keep = groups.filter(({ field }) => {
    if (!drop || responses.length === 0) return true;
    return responses.some((response) => {
      const answered = response.fields[field.id];
      return answered !== undefined && !answered.state.hiddenByRule;
    });
  });

  return {
    columns: keep.flatMap((group) => group.columns),
    rows: responses.map((response) =>
      keep.flatMap(({ field, columns }) => cellsFor(field, response, columns.length)),
    ),
  };
}
